import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingServices() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', code:'', service_type:'standard', description:'', status:'draft', notes:'' });

  useEffect(() => { if (companyId) fetchServices(); }, [companyId, page, typeFilter, statusFilter]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchServices = async () => {
    setLoading(true);
    let url = `${API}/companies/${companyId}/outsourcing/services?page=${page}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (typeFilter) url += `&service_type=${typeFilter}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    const res = await authFetch(url);
    if (!res) return;
    const json = await res.json();
    setServices(json.data || []);
    setLastPage(json.last_page || 1);
    setLoading(false);
  };

  const openService = async (svc) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/services/${svc.id}`);
    if (!res) return;
    setSelected(await res.json());
  };

  const saveService = async () => {
    const method = selected ? 'PUT' : 'POST';
    const url = selected ? `${API}/companies/${companyId}/outsourcing/services/${selected.id}` : `${API}/companies/${companyId}/outsourcing/services`;
    const res = await authFetch(url, { method, headers:{'Content-Type':'application/json'}, body: JSON.stringify(form) });
    if (!res) return;
    if (res.ok) { setShowForm(false); setSelected(null); fetchServices(); }
    else { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const archiveService = async (id) => {
    if (!window.confirm('Archive this service?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/services/${id}/archive`, { method:'POST' });
    if (res && res.ok) { setSelected(null); fetchServices(); }
  };

  const deleteService = async (id) => {
    if (!window.confirm('Delete this service? Only draft services with no usage can be deleted.')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/services/${id}`, { method:'DELETE' });
    if (res && res.ok) { setSelected(null); fetchServices(); }
    else if (res) { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  if (!companyId) return <div style={st.page}><p>Please select a company first.</p></div>;

  // ── DETAIL VIEW ──
  if (selected) {
    return (
      <div style={st.page}>
        <button onClick={() => setSelected(null)} style={st.backBtn}>← Back to Services</button>
        <div style={st.detailHeader}>
          <h2 style={st.title}>{selected.name} <span style={{fontSize:14,color:'#999'}}>({selected.code})</span></h2>
          <div style={{display:'flex',gap:8}}>
            <span style={{...st.badge, backgroundColor: typeClr(selected.service_type)}}>{selected.service_type}</span>
            <span style={{...st.badge, backgroundColor: statusClr(selected.status)}}>{selected.status}</span>
            <button onClick={() => { setForm({name:selected.name,code:selected.code,service_type:selected.service_type,description:selected.description||'',status:selected.status,notes:selected.notes||''}); setShowForm(true); }} style={st.btnPrimary}>Edit</button>
            {selected.status !== 'archived' && <button onClick={() => archiveService(selected.id)} style={st.btnWarn}>Archive</button>}
            {selected.status === 'draft' && <button onClick={() => deleteService(selected.id)} style={st.btnDanger}>Delete</button>}
          </div>
        </div>

        <div style={st.section}>
          <p><strong>Type:</strong> {selected.service_type}</p>
          <p><strong>Description:</strong> {selected.description || '—'}</p>
          <p><strong>Notes:</strong> {selected.notes || '—'}</p>
          <p><strong>Active Clients:</strong> {selected.active_client_count || 0}</p>
        </div>

        {/* Client Services */}
        {selected.client_services && selected.client_services.length > 0 && (
          <div style={st.section}>
            <h3 style={{marginTop:0,marginBottom:12}}>Assigned to Clients</h3>
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={st.th}>Client</th>
                    <th style={st.th}>Price</th>
                    <th style={st.th}>Status</th>
                    <th style={st.th}>Start Date</th>
                    <th style={st.th}>End Date</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.client_services.map((cs, i) => (
                    <tr key={cs.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                      <td style={st.td}>{cs.client?.name || '—'}</td>
                      <td style={st.td}>${cs.current_price}</td>
                      <td style={st.td}><span style={{...st.badge, backgroundColor: cs.status==='active' ? '#27ae60' : '#e74c3c'}}>{cs.status}</span></td>
                      <td style={st.td}>{cs.effective_start_date || '—'}</td>
                      <td style={st.td}>{cs.effective_end_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showForm && renderModal(selected ? 'Edit Service' : 'New Service', () => setShowForm(false), <>
          <label style={st.label}>Name *</label>
          <input value={form.name} onChange={e => setForm({...form,name:e.target.value})} style={st.input} />
          <label style={st.label}>Code</label>
          <input value={form.code} onChange={e => setForm({...form,code:e.target.value})} style={st.input} placeholder="Auto-generated if empty" />
          <label style={st.label}>Type *</label>
          <select value={form.service_type} onChange={e => setForm({...form,service_type:e.target.value})} style={st.input}>
            <option value="standard">Standard</option>
            <option value="extra">Extra</option>
          </select>
          <label style={st.label}>Description</label>
          <textarea value={form.description} onChange={e => setForm({...form,description:e.target.value})} style={{...st.input,minHeight:60}} />
          <label style={st.label}>Status</label>
          <select value={form.status} onChange={e => setForm({...form,status:e.target.value})} style={st.input}>
            {['draft','active','inactive','archived'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <label style={st.label}>Notes</label>
          <textarea value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} style={{...st.input,minHeight:50}} />
          <button onClick={saveService} style={st.btnPrimary}>Save</button>
        </>)}
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div style={st.page}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:16}}>
        <h2 style={st.title}>Outsourcing Services</h2>
        <button onClick={() => { setForm({name:'',code:'',service_type:'standard',description:'',status:'draft',notes:''}); setSelected(null); setShowForm(true); }} style={st.btnPrimary}>+ New Service</button>
      </div>

      <div style={st.filterRow}>
        <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && fetchServices()} style={{...st.input,maxWidth:250}} />
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{...st.input,width:140}}>
          <option value="">All Types</option>
          <option value="standard">Standard</option>
          <option value="extra">Extra</option>
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{...st.input,width:140}}>
          <option value="">All Statuses</option>
          {['draft','active','inactive','archived'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => { setPage(1); fetchServices(); }} style={st.btnPrimary}>Search</button>
      </div>

      {initialLoading ? <p>Loading...</p> : services.length === 0 ? <p style={{color:'#999'}}>No services found.</p> : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Name</th>
                <th style={st.th}>Code</th>
                <th style={st.th}>Type</th>
                <th style={st.th}>Status</th>
                <th style={st.th}>Active Clients</th>
                <th style={st.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc, i) => (
                <tr key={svc.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                  <td style={st.td}><span style={{cursor:'pointer',color:'#2980b9',fontWeight:600}} onClick={() => openService(svc)}>{svc.name}</span></td>
                  <td style={st.td}>{svc.code}</td>
                  <td style={st.td}><span style={{...st.badge, backgroundColor:typeClr(svc.service_type)}}>{svc.service_type}</span></td>
                  <td style={st.td}><span style={{...st.badge, backgroundColor:statusClr(svc.status)}}>{svc.status}</span></td>
                  <td style={st.td}>{svc.active_client_count || 0}</td>
                  <td style={st.td}><button onClick={() => openService(svc)} style={st.btnSm}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div style={st.pagination}>
          <button disabled={page<=1} onClick={() => setPage(page-1)} style={st.pageBtn}>Prev</button>
          <span>Page {page} of {lastPage}</span>
          <button disabled={page>=lastPage} onClick={() => setPage(page+1)} style={st.pageBtn}>Next</button>
        </div>
      )}

      {showForm && !selected && renderModal('New Service', () => setShowForm(false), <>
        <label style={st.label}>Name *</label>
        <input value={form.name} onChange={e => setForm({...form,name:e.target.value})} style={st.input} />
        <label style={st.label}>Code</label>
        <input value={form.code} onChange={e => setForm({...form,code:e.target.value})} style={st.input} placeholder="Auto-generated if empty" />
        <label style={st.label}>Type *</label>
        <select value={form.service_type} onChange={e => setForm({...form,service_type:e.target.value})} style={st.input}>
          <option value="standard">Standard</option>
          <option value="extra">Extra</option>
        </select>
        <label style={st.label}>Description</label>
        <textarea value={form.description} onChange={e => setForm({...form,description:e.target.value})} style={{...st.input,minHeight:60}} />
        <label style={st.label}>Notes</label>
        <textarea value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} style={{...st.input,minHeight:50}} />
        <button onClick={saveService} style={st.btnPrimary}>Create</button>
      </>)}
    </div>
  );
}

function renderModal(title, onClose, children) {
  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{margin:0}}>{title}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function typeClr(t) { return t === 'standard' ? '#2980b9' : '#e67e22'; }
function statusClr(st) {
  const m = { draft:'#95a5a6', active:'#27ae60', inactive:'#f39c12', archived:'#7f8c8d' };
  return m[st] || '#95a5a6';
}

const st = {
  page: { padding:24, maxWidth:1200, margin:'0 auto' },
  title: { fontSize:24, fontWeight:700, margin:0 },
  filterRow: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  input: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14, width:'100%', boxSizing:'border-box', marginBottom:8 },
  label: { display:'block', fontSize:13, fontWeight:600, marginBottom:4, marginTop:8 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  btnPrimary: { padding:'8px 16px', background:'#3498db', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:600 },
  btnWarn: { padding:'8px 16px', background:'#f39c12', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14 },
  btnDanger: { padding:'8px 16px', background:'#e74c3c', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14 },
  btnSm: { padding:'4px 10px', background:'#ecf0f1', border:'none', borderRadius:4, cursor:'pointer', fontSize:13 },
  backBtn: { background:'none', border:'none', color:'#3498db', cursor:'pointer', fontSize:14, fontWeight:600, marginBottom:12, padding:0 },
  detailHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:16 },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'#fff', borderRadius:12, padding:24, width:'90%', maxWidth:500, maxHeight:'85vh', overflowY:'auto' },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:16, marginTop:16 },
  pageBtn: { padding:'6px 14px', border:'1px solid #ccc', borderRadius:6, background:'#fff', cursor:'pointer' },
};
